import { useEffect, useState } from "react";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { toast } from "sonner";
import { formatINR } from "../lib/utils-format";
import {
    CheckCircle2, XCircle, Clock, AlertCircle, ShieldCheck,
    ArrowDownLeft, ArrowDownRight, TrendingUp, Gift, Wallet,
    Search, RefreshCw,
} from "lucide-react";

/* ── KYC status badge ──────────────────────────────────────────────── */
function KYCBadge({ status }) {
    const map = {
        approved:      { cls: "bg-emerald-100 text-emerald-700 border-emerald-200",  icon: CheckCircle2, label: "Approved"      },
        pending:       { cls: "bg-blue-100 text-blue-700 border-blue-200",           icon: Clock,        label: "Pending"       },
        rejected:      { cls: "bg-red-100 text-red-700 border-red-200",              icon: XCircle,      label: "Rejected"      },
        not_submitted: { cls: "bg-slate-100 text-slate-500 border-slate-200",        icon: AlertCircle,  label: "Not submitted" },
    };
    const { cls, icon: Icon, label } = map[status] || map.not_submitted;
    return (
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-bold ${cls}`}>
            <Icon className="w-3 h-3" /> {label}
        </span>
    );
}

/* ── summary stat card ─────────────────────────────────────────────── */
function MiniStat({ label, value, color = "slate" }) {
    const c = {
        slate:   "text-slate-700",
        emerald: "text-emerald-600",
        blue:    "text-[#2563EB]",
        amber:   "text-amber-600",
        orange:  "text-orange-500",
    }[color] || "text-slate-700";
    return (
        <div className="text-center">
            <div className={`font-black text-lg leading-tight ${c}`}>{value}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">{label}</div>
        </div>
    );
}

export default function AdminKYC() {
    const [members, setMembers]   = useState([]);
    const [loading, setLoading]   = useState(false);
    const [kycing,  setKycing]    = useState({});
    const [filter,  setFilter]    = useState("all");   // all | not_submitted | pending | approved | rejected
    const [q,       setQ]         = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/bonus-overview");
            setMembers(data);
        } catch {
            toast.error("Failed to load member data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const approveKYC = async (id, name) => {
        setKycing(p => ({ ...p, [id]: true }));
        try {
            await api.post(`/admin/kyc/user/${id}/action`, { action: "approve" });
            toast.success(`KYC approved for ${name}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Approval failed");
        } finally {
            setKycing(p => ({ ...p, [id]: false }));
        }
    };

    const rejectKYC = async (id, name) => {
        const reason = window.prompt(`Rejection reason for ${name} (required):`);
        if (!reason) return;
        setKycing(p => ({ ...p, [id]: true }));
        try {
            await api.post(`/admin/kyc/user/${id}/action`, { action: "reject", reason });
            toast.success(`KYC rejected for ${name}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Rejection failed");
        } finally {
            setKycing(p => ({ ...p, [id]: false }));
        }
    };

    /* ── filtered list ── */
    const filtered = members.filter(m => {
        const matchFilter = filter === "all" || m.kyc_status === filter;
        const matchQ      = !q || m.full_name.toLowerCase().includes(q.toLowerCase()) || m.email?.toLowerCase().includes(q.toLowerCase());
        return matchFilter && matchQ;
    });

    /* ── aggregate totals ── */
    const totalDirect   = members.reduce((a, m) => a + m.direct_bonus, 0);
    const totalPairing  = members.reduce((a, m) => a + m.pairing_bonus, 0);
    const totalEarned   = members.reduce((a, m) => a + m.total_earned, 0);
    const totalWithdrew = members.reduce((a, m) => a + m.withdrawn, 0);
    const kycByStatus   = members.reduce((acc, m) => { acc[m.kyc_status] = (acc[m.kyc_status] || 0) + 1; return acc; }, {});

    const FILTERS = [
        { key: "all",          label: "All",          count: members.length },
        { key: "not_submitted",label: "Not Submitted", count: kycByStatus.not_submitted  || 0 },
        { key: "pending",      label: "Pending",       count: kycByStatus.pending        || 0 },
        { key: "approved",     label: "Approved",      count: kycByStatus.approved       || 0 },
        { key: "rejected",     label: "Rejected",      count: kycByStatus.rejected       || 0 },
    ];

    return (
        <AppShell admin>
            {/* ── header ── */}
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="overline">Admin</div>
                    <h1 className="font-display font-black text-3xl tracking-tighter text-slate-900 mt-2">
                        KYC &amp; Bonus Overview
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Approve or reject member KYC · View individual and overall bonus earnings.
                    </p>
                </div>
                <button
                    onClick={load}
                    className="flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                    <RefreshCw className="w-4 h-4" /> Refresh
                </button>
            </div>

            {/* ── platform totals ── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-[#1B2B6B] text-white rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <Gift className="w-4 h-4 text-blue-200" />
                        <span className="overline text-blue-300">Direct Bonuses Paid</span>
                    </div>
                    <div className="font-display font-black text-2xl text-blue-200">{formatINR(totalDirect)}</div>
                    <div className="text-xs text-blue-300 mt-1">across all members</div>
                </div>
                <div className="bg-[#1B2B6B] text-white rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-blue-200" />
                        <span className="overline text-blue-300">Pairing Bonuses Paid</span>
                    </div>
                    <div className="font-display font-black text-2xl text-blue-200">{formatINR(totalPairing)}</div>
                    <div className="text-xs text-blue-300 mt-1">across all members</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <Wallet className="w-4 h-4 text-[#2563EB]" />
                        <span className="overline text-slate-500">Total Earned</span>
                    </div>
                    <div className="font-display font-black text-2xl text-[#2563EB]">{formatINR(totalEarned)}</div>
                    <div className="text-xs text-slate-400 mt-1">direct + pairing + travel fund</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                    <div className="overline text-slate-500 mb-3">KYC Summary</div>
                    <div className="grid grid-cols-2 gap-y-1 text-xs">
                        <span className="text-emerald-600 font-semibold">✓ Approved</span>
                        <span className="font-bold text-right">{kycByStatus.approved || 0}</span>
                        <span className="text-blue-600 font-semibold">⟳ Pending</span>
                        <span className="font-bold text-right">{kycByStatus.pending || 0}</span>
                        <span className="text-slate-500 font-semibold">— Not submitted</span>
                        <span className="font-bold text-right">{kycByStatus.not_submitted || 0}</span>
                        <span className="text-red-600 font-semibold">✕ Rejected</span>
                        <span className="font-bold text-right">{kycByStatus.rejected || 0}</span>
                    </div>
                </div>
            </div>

            {/* ── filters + search ── */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex flex-wrap gap-1.5">
                    {FILTERS.map(f => (
                        <button
                            key={f.key}
                            onClick={() => setFilter(f.key)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                                filter === f.key
                                    ? "bg-[#1B2B6B] text-white border-[#1B2B6B]"
                                    : "bg-white text-slate-600 border-slate-200 hover:border-[#2563EB] hover:text-[#2563EB]"
                            }`}
                        >
                            {f.label}
                            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                                filter === f.key ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                            }`}>{f.count}</span>
                        </button>
                    ))}
                </div>
                <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        value={q}
                        onChange={e => setQ(e.target.value)}
                        placeholder="Search name or email"
                        className="h-9 pl-9 pr-4 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#2563EB] w-52"
                    />
                </div>
            </div>

            {/* ── main table ── */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gradient-to-r from-[#1B2B6B] to-[#2A3D82] text-white">
                            <th className="text-left font-semibold py-3 px-4 text-xs uppercase tracking-wider">Member</th>
                            <th className="text-center font-semibold py-3 px-3 text-xs uppercase tracking-wider">Wing</th>
                            <th className="text-center font-semibold py-3 px-3 text-xs uppercase tracking-wider">
                                <ArrowDownLeft className="inline w-3 h-3 text-blue-200" /> L /
                                <ArrowDownRight className="inline w-3 h-3 text-white/70" /> R
                            </th>
                            <th className="text-center font-semibold py-3 px-3 text-xs uppercase tracking-wider">KYC Status</th>
                            <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider">Direct Bonus</th>
                            <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider">Pairing Bonus</th>
                            <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider">Total Earned</th>
                            <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider">Available</th>
                            <th className="text-right font-semibold py-3 px-4 text-xs uppercase tracking-wider">KYC Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={9} className="text-center py-12 text-slate-400">Loading…</td></tr>
                        )}
                        {!loading && filtered.length === 0 && (
                            <tr><td colSpan={9} className="text-center py-12 text-slate-400">No members match this filter.</td></tr>
                        )}
                        {filtered.map(m => (
                            <tr key={m.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                                {/* Name + email + code */}
                                <td className="py-3 px-4">
                                    <div className="font-semibold text-slate-900">{m.full_name}</div>
                                    <div className="text-xs text-slate-400 mt-0.5">{m.email}</div>
                                    <div className="font-mono text-[10px] text-[#2563EB] mt-0.5">{m.referral_code}</div>
                                </td>

                                {/* Wing side */}
                                <td className="py-3 px-3 text-center text-xs font-bold">
                                    {m.side === "L" && <span className="wing-l">Left</span>}
                                    {m.side === "R" && <span className="wing-r">Right</span>}
                                    {!m.side && <span className="text-slate-300">—</span>}
                                </td>

                                {/* L / R counts */}
                                <td className="py-3 px-3 text-center font-mono text-xs">
                                    <span className="wing-l font-bold">{m.left_count}</span>
                                    <span className="text-slate-300 mx-1">/</span>
                                    <span className="wing-r font-bold">{m.right_count}</span>
                                </td>

                                {/* KYC badge */}
                                <td className="py-3 px-3 text-center">
                                    <KYCBadge status={m.kyc_status} />
                                </td>

                                {/* Direct bonus */}
                                <td className="py-3 px-3 text-right font-mono text-sm">
                                    {m.direct_bonus > 0
                                        ? <span className="text-[#2563EB] font-semibold">{formatINR(m.direct_bonus)}</span>
                                        : <span className="text-slate-300">—</span>
                                    }
                                </td>

                                {/* Pairing bonus */}
                                <td className="py-3 px-3 text-right font-mono text-sm">
                                    {m.pairing_bonus > 0
                                        ? <span className="text-[#2563EB] font-semibold">{formatINR(m.pairing_bonus)}</span>
                                        : <span className="text-slate-300">—</span>
                                    }
                                </td>

                                {/* Total earned */}
                                <td className="py-3 px-3 text-right font-mono font-black text-sm text-[#2563EB]">
                                    {m.total_earned > 0 ? formatINR(m.total_earned) : <span className="text-slate-300 font-normal">₹0</span>}
                                    {m.travel_fund_total > 0 && (
                                        <div className="text-[10px] font-normal text-blue-400 mt-0.5">
                                            incl. {formatINR(m.travel_fund_total)} travel fund
                                        </div>
                                    )}
                                </td>

                                {/* Available balance */}
                                <td className="py-3 px-3 text-right font-mono text-sm">
                                    <div className={m.available_balance > 0 ? "text-emerald-600 font-semibold" : "text-slate-300"}>
                                        {formatINR(m.available_balance)}
                                    </div>
                                    {m.pending_balance > 0 && (
                                        <div className="text-[10px] text-amber-500">+{formatINR(m.pending_balance)} pending</div>
                                    )}
                                </td>

                                {/* KYC actions */}
                                <td className="py-3 px-4 text-right">
                                    <div className="inline-flex gap-1.5">
                                        {m.kyc_status !== "approved" && (
                                            <button
                                                disabled={kycing[m.id]}
                                                onClick={() => approveKYC(m.id, m.full_name)}
                                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white disabled:opacity-50 transition-colors"
                                            >
                                                <ShieldCheck className="w-3 h-3" />
                                                {kycing[m.id] ? "…" : "Approve"}
                                            </button>
                                        )}
                                        {m.kyc_status !== "rejected" && m.kyc_status !== "not_submitted" && (
                                            <button
                                                disabled={kycing[m.id]}
                                                onClick={() => rejectKYC(m.id, m.full_name)}
                                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold rounded-lg bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 disabled:opacity-50 transition-colors"
                                            >
                                                <XCircle className="w-3 h-3" />
                                                Reject
                                            </button>
                                        )}
                                        {m.kyc_status === "approved" && (
                                            <button
                                                disabled={kycing[m.id]}
                                                onClick={() => rejectKYC(m.id, m.full_name)}
                                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold rounded-lg bg-blue-50 hover:bg-blue-100 text-[#1B2B6B] border border-blue-200 disabled:opacity-50 transition-colors"
                                            >
                                                <XCircle className="w-3 h-3" />
                                                Revoke
                                            </button>
                                        )}
                                        {(m.kyc_status === "not_submitted") && (
                                            <span className="text-xs text-slate-300 italic">No docs</span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>

                    {/* Totals footer */}
                    {filtered.length > 0 && (
                        <tfoot>
                            <tr className="bg-gradient-to-r from-[#1B2B6B] to-[#2A3D82] text-white text-sm font-bold">
                                <td colSpan={4} className="py-3 px-4 text-blue-200">
                                    Totals ({filtered.length} member{filtered.length !== 1 ? "s" : ""})
                                </td>
                                <td className="py-3 px-3 text-right font-mono text-blue-200">
                                    {formatINR(filtered.reduce((a, m) => a + m.direct_bonus, 0))}
                                </td>
                                <td className="py-3 px-3 text-right font-mono text-blue-200">
                                    {formatINR(filtered.reduce((a, m) => a + m.pairing_bonus, 0))}
                                </td>
                                <td className="py-3 px-3 text-right font-mono text-white">
                                    {formatINR(filtered.reduce((a, m) => a + m.total_earned, 0))}
                                    <div className="text-[10px] font-normal text-blue-200 mt-0.5">
                                        incl. {formatINR(filtered.reduce((a, m) => a + m.travel_fund_total, 0))} travel fund
                                    </div>
                                </td>
                                <td className="py-3 px-3 text-right font-mono text-white/70">
                                    {formatINR(filtered.reduce((a, m) => a + m.available_balance, 0))}
                                </td>
                                <td className="py-3 px-4" />
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </AppShell>
    );
}
